import { inject, Injectable } from '@angular/core';

import { JwtInfo } from '@hsc/common/schemas/eua.schema.js';
import { normalizeJwtContext } from '@hsc/common/utilities/jwt-utils';
import { NewRelicService } from '@services/new-relic.service';
import { SplunkLoggerService } from '@services/splunk-logger.service';
import { environment } from '@environments/environment';

export type FullStoryEventProps = Record<string, string | number | boolean | null | undefined>;

/*
 * https://github.com/Nationwide/fullstory/wiki
 *
 * Please note: We caution everyone to not use the FS.identify API as it impacts users for the entire Org you are in.
 * For this reason we strongly recommend that you always use FS.setUserVars instead of FS.identify.
 * If you feel that you need to use FS.identify please reach out to NWFullStoryTeam@nationwide.com before using it.
 */

@Injectable({ providedIn: 'root' })
export class FullStoryService {
  // Properties
  private readonly splunkLoggerService = inject(SplunkLoggerService);
  private readonly newRelicService = inject(NewRelicService);

  // Getters/Setters
  private get fs(): typeof window.FS | null {
    return window.FS ?? null;
  }

  // Methods
  initialize() {
    // Stop GTM-loaded FullStory from recording even though the script is present
    if (!environment.FULLSTORY_ENABLED) {
      this.splunkLoggerService.warn('FullStory disabled');
      if (window.FS) {
        window.FS?.shutdown?.();
      } else {
        window._fs_ready = () => window.FS?.shutdown?.();
      }
      return;
    }

    if (window.FS) {
      this.splunkLoggerService.info('FullStory initialized');
      return;
    }

    // Possible failures include ad blockers, privacy extensions, and network issues.
    const failureTimer = setTimeout(() => {
      if (!window.FS) {
        const error = new Error('FullStory failed to initialize');
        this.splunkLoggerService.error(error);
        this.newRelicService.noticeError(error);
      }
    }, 20000);

    window._fs_ready = () => {
      clearTimeout(failureTimer);
      this.splunkLoggerService.info('FullStory initialized via _fs_ready callback');
    };
  }

  // https://developer.fullstory.com/browser/v1/identification/set-user-properties/
  setUserVars(jwtInfo: JwtInfo) {
    if (!environment.FULLSTORY_ENABLED) return;

    try {
      const fullStoryVars = normalizeJwtContext(jwtInfo);

      // Add this derived key/value pair for convenience in Fullstory dashboards and searches
      fullStoryVars['displayName'] = `${fullStoryVars['firstName']} ${fullStoryVars['lastName']}`;

      this.fs?.setUserVars(fullStoryVars);
      this.splunkLoggerService.info('FullStory user identified');
    } catch (err) {
      this.splunkLoggerService.error(err, { message: 'Error calling FS.setUserVars' });
      this.newRelicService.noticeError(err);
    }
  }

  // https://developer.fullstory.com/browser/v1/get-session-details/
  getCurrentSessionUrl(): string | null | undefined {
    if (!environment.FULLSTORY_ENABLED) return null;

    try {
      const url = this.fs?.getCurrentSessionURL(true);
      this.splunkLoggerService.info(`FullStory current session URL:`, { url });
      return url;
    } catch (err) {
      this.splunkLoggerService.error(err, { message: 'Error calling FS.getCurrentSessionURL' });
      this.newRelicService.noticeError(err);
      return null;
    }
  }

  // TODO: add event name validation for auditing events
  // https://developer.fullstory.com/browser/v1/capture-events/analytics-events/
  event(name: string, props?: FullStoryEventProps) {
    if (!environment.FULLSTORY_ENABLED) return;

    try {
      // Tag every event so dashboards can segment by app across the FullStory Org
      this.fs?.event(name, { application: 'home-service-contract', ...props });
    } catch (err) {
      this.splunkLoggerService.error(err, { message: 'Error calling FS.event' });
      this.newRelicService.noticeError(err);
    }
  }
}
